/**
 * ClipboardFormat.ts - Format detection and conversion utilities
 * 
 * CHANGE HISTORY:
 * - 251102_1145 [pending] danielbeer: Created format detection and conversion utilities
 * 
 * Handles clipboard format detection, validation, and conversion between formats.
 */

import type {
  ClipboardFormat,
  FormatDetectionResult,
  FormatConversionOptions
} from '../types';

/**
 * Clipboard format utilities
 */
export class ClipboardFormatUtil {
  
  /**
   * Detect format from data
   */
  static detect(data: any): FormatDetectionResult {
    // String detection
    if (typeof data === 'string') {
      return this.detectStringFormat(data);
    }
    
    // Blob detection
    if (data instanceof Blob) {
      return {
        format: data.type as ClipboardFormat,
        confidence: 1.0
      };
    }
    
    // File detection
    if (data instanceof File) {
      return {
        format: data.type as ClipboardFormat,
        confidence: 1.0
      };
    }
    
    // Object detection (JSON)
    if (typeof data === 'object' && data !== null) {
      return {
        format: 'application/json',
        confidence: 0.9
      };
    }
    
    // Fallback
    return {
      format: 'application/octet-stream',
      confidence: 0.5
    };
  }
  
  /**
   * Detect format from string content
   */
  private static detectStringFormat(str: string): FormatDetectionResult {
    // FileMaker XML
    if (str.includes('<fmxmlsnippet')) {
      return {
        format: 'application/x-filemaker-xml',
        confidence: 1.0
      };
    }
    
    // HTML
    if (str.trim().startsWith('<') && (str.includes('</') || str.includes('/>'))) {
      return {
        format: 'text/html',
        confidence: 0.9,
        alternatives: ['text/plain']
      };
    }
    
    // JSON
    if (this.isJSON(str)) {
      return {
        format: 'application/json',
        confidence: 0.95,
        alternatives: ['text/plain']
      };
    }
    
    // RTF
    if (str.startsWith('{\\rtf')) {
      return {
        format: 'text/rtf',
        confidence: 1.0
      };
    }
    
    // Plain text (default)
    return {
      format: 'text/plain',
      confidence: 0.8
    };
  }
  
  /**
   * Check if string is valid JSON
   */
  private static isJSON(str: string): boolean {
    try {
      JSON.parse(str);
      return true;
    } catch {
      return false;
    }
  }
  
  /**
   * Validate format
   */
  static isValid(format: string): boolean {
    const validFormats = [
      'text/plain',
      'text/html',
      'text/rtf',
      'image/png',
      'image/jpeg',
      'image/gif',
      'image/webp',
      'application/json',
      'application/x-filemaker-xml',
      'application/octet-stream'
    ];
    
    return validFormats.includes(format) || format.startsWith('text/') || format.startsWith('image/');
  }
  
  /**
   * Validate data for a specific format
   */
  static validate(data: any, format: ClipboardFormat): boolean {
    if (!data) return false;
    
    switch (format) {
      case 'text/plain':
        return typeof data === 'string';
      
      case 'text/html':
        return typeof data === 'string' && (data.includes('<') || data.includes('>'));
      
      case 'application/json':
        if (typeof data === 'object') return true;
        try {
          JSON.parse(data);
          return true;
        } catch {
          return false;
        }
      
      case 'application/x-filemaker-xml':
        return typeof data === 'string' && data.includes('fmxmlsnippet');
      
      default:
        return true;
    }
  }
  
  /**
   * Convert data between formats
   */
  static async convert(
    data: any,
    fromFormat: ClipboardFormat,
    toFormat: ClipboardFormat,
    options: FormatConversionOptions = {}
  ): Promise<any> {
    // Same format - no conversion needed
    if (fromFormat === toFormat) {
      return data;
    }
    
    // Text conversions
    if (fromFormat === 'text/html' && toFormat === 'text/plain') {
      return this.htmlToText(data);
    }
    
    if (fromFormat === 'text/plain' && toFormat === 'text/html') {
      return this.textToHtml(data);
    }
    
    // JSON conversions
    if (fromFormat === 'application/json' && toFormat === 'text/plain') {
      return JSON.stringify(data, null, 2);
    }
    
    if (fromFormat === 'text/plain' && toFormat === 'application/json') {
      try {
        return JSON.parse(data);
      } catch (error) {
        throw new Error('Invalid JSON string');
      }
    }
    
    // FileMaker XML to text
    if (fromFormat === 'application/x-filemaker-xml' && toFormat === 'text/plain') {
      return data; // XML is already text
    }
    
    // Image conversions (if needed in future)
    if (fromFormat.startsWith('image/') && toFormat.startsWith('image/')) {
      return this.convertImage(data, fromFormat, toFormat, options);
    }
    
    // No conversion available
    throw new Error(`Cannot convert from ${fromFormat} to ${toFormat}`);
  }
  
  /**
   * Convert HTML to plain text
   */
  private static htmlToText(html: string): string {
    // Create temporary element
    const temp = document.createElement('div');
    temp.innerHTML = html;
    return temp.textContent || temp.innerText || '';
  }
  
  /**
   * Convert plain text to HTML
   */
  private static textToHtml(text: string): string {
    const escaped = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    
    // Split by newlines and wrap in paragraphs
    const paragraphs = escaped
      .split('\n')
      .filter(line => line.trim())
      .map(line => `<p>${line}</p>`)
      .join('');
    
    return paragraphs || '<p></p>';
  }
  
  /**
   * Convert between image formats
   */
  private static async convertImage(
    data: Blob,
    fromFormat: ClipboardFormat,
    toFormat: ClipboardFormat,
    options: FormatConversionOptions
  ): Promise<Blob> {
    // Create image element
    const img = new Image();
    const url = URL.createObjectURL(data);
    
    return new Promise((resolve, reject) => {
      img.onload = () => {
        // Create canvas
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Cannot get canvas context'));
          return;
        }
        
        // Draw image
        ctx.drawImage(img, 0, 0);
        
        // Convert to blob
        canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(url);
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to convert image'));
            }
          },
          toFormat,
          options.quality || 0.92
        );
      };
      
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image'));
      };
      
      img.src = url;
    });
  }
  
  /**
   * Get MIME type description
   */
  static getDescription(format: ClipboardFormat): string {
    const descriptions: Record<string, string> = {
      'text/plain': 'Plain Text',
      'text/html': 'HTML',
      'text/rtf': 'Rich Text Format',
      'image/png': 'PNG Image',
      'image/jpeg': 'JPEG Image',
      'image/gif': 'GIF Image',
      'image/webp': 'WebP Image',
      'application/json': 'JSON Data',
      'application/x-filemaker-xml': 'FileMaker XML',
      'application/octet-stream': 'Binary Data'
    };
    
    return descriptions[format] || format;
  }
  
  /**
   * Check if format is text-based
   */
  static isText(format: ClipboardFormat): boolean {
    return format.startsWith('text/') || 
           format === 'application/json' ||
           format === 'application/x-filemaker-xml';
  }
  
  /**
   * Check if format is image-based
   */
  static isImage(format: ClipboardFormat): boolean {
    return format.startsWith('image/');
  }
  
  /**
   * Get file extension for format
   */
  static getExtension(format: ClipboardFormat): string {
    const extensions: Record<string, string> = {
      'text/plain': 'txt',
      'text/html': 'html',
      'text/rtf': 'rtf',
      'image/png': 'png',
      'image/jpeg': 'jpg',
      'image/gif': 'gif',
      'image/webp': 'webp',
      'application/json': 'json',
      'application/x-filemaker-xml': 'xml',
      'application/octet-stream': 'bin'
    };
    
    return extensions[format] || 'dat';
  }
}
